import { ColumnDetails, ColumnType } from '../../../../../../../../grid/types/common';
import { translateFn } from '../../../../../../../../utils/translator.helper';
import {
  getAntimalwareSettingsExclusionsModuleTypeFormOptions,
  getFullAntimalwareSettingsExclusionsTaskTypeFormOptions,
} from '../../../../../utils/uiSettings/antimalware/antimalware.values';
import { html, TemplateResult } from 'lit';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import { EnumListItem } from '../../../../../../../../ui/gzEnumList/types';
import { getLabelForEnumValue } from '../../../../../../../../grid/utils/columns';
import { dataGridStrings } from '../../../../../../../../grid/utils/gridStrings';
import { getAvailableModuleOptionsByType } from './utils/configs';
import { AntimalwareSettingsExclusionsItem } from '@nirvana/shared/dtos/types/policies/common';

export enum Column {
  TYPE = 1,
  PATH,
  MODULE,
  REMARKS,
}

export enum ColumnKeys {
  TYPE = 'taskType',
  PATH = 'path',
  MODULE = 'module',
  REMARKS = 'remarks',
}

export const GRID_COLUMNS = [Column.TYPE, Column.PATH, Column.MODULE, Column.REMARKS];

export const FILTERS = [
  { [ColumnKeys.TYPE]: { in: [] } },
  { [ColumnKeys.PATH]: { contains: '' } },
  { [ColumnKeys.MODULE]: { in: [] } },
  { [ColumnKeys.REMARKS]: { contains: '' } },
];
export const PREDEFINED_FILTERS_KEYS = [ColumnKeys.TYPE, ColumnKeys.PATH, ColumnKeys.MODULE, ColumnKeys.REMARKS];

export function getGridColumnDetails(): Array<ColumnDetails<Column, ColumnKeys>> {
  return [
    {
      column: Column.TYPE,
      key: ColumnKeys.TYPE,
      columnName: translateFn('Type'),
      type: ColumnType.ENUM,
      values: getFullAntimalwareSettingsExclusionsTaskTypeFormOptions(),
      removable: false,
      filteringDisabled: true,
      sortable: true,
      editingDisabled: true,
    },
    {
      column: Column.PATH,
      key: ColumnKeys.PATH,
      columnName: translateFn('Excluded items'),
      type: ColumnType.STRING,
      removable: false,
      filteringDisabled: true,
      sortable: true,
    },
    {
      column: Column.MODULE,
      key: ColumnKeys.MODULE,
      columnName: translateFn('Modules'),
      type: ColumnType.ENUM,
      values: getAntimalwareSettingsExclusionsModuleTypeFormOptions(),
      removable: false,
      filteringDisabled: true,
      sortable: true,
    },
    {
      column: Column.REMARKS,
      key: ColumnKeys.REMARKS,
      columnName: translateFn('Description'),
      type: ColumnType.STRING,
      removable: false,
      filteringDisabled: true,
      sortable: true,
    },
  ];
}

export function modulesCellCustomRenderer(
  gridController,
  rowData: { item: AntimalwareSettingsExclusionsItem; index: number },
  columnDetails
): TemplateResult {
  const activeItem = gridController?.view.getEditedGridItem();
  const isActive = activeItem && activeItem.index === rowData.index;
  let cellValues, taskType;

  if (isActive) {
    taskType = activeItem.item.taskType;
    cellValues = activeItem.item[columnDetails.key];
  } else {
    taskType = rowData.item.taskType;
    cellValues = rowData.item[columnDetails.key];
    if (isEmpty(cellValues)) {
      return html`-`;
    }
  }

  const availableOptions = getAvailableModuleOptionsByType(taskType, false);
  const availableOptionsWithRestriction = getAvailableModuleOptionsByType(taskType, true);
  const dropdownOptions = columnDetails.values
    .filter(module => availableOptions.includes(module.value))
    .map(module => {
      // match cell values against all the module values so we can get the selected items for the dropdown
      const matches = cellValues.filter(value => value === module.value);
      const selected = !!matches.length;
      const entry: EnumListItem = {
        label: module.label,
        value: module.value,
        parent: module.parent,
        selected: selected,
        disabled: !selected && !availableOptionsWithRestriction.includes(module.value),
      };

      return entry;
    });

  const labels = cellValues.map(val => getLabelForEnumValue(columnDetails, val)).join(', ');
  const { confirmString, allItemsLabel } = dataGridStrings().enumColumnStrings;

  return html`<gz-grid-cell-edit
    class="cell-label"
    .type="${columnDetails.type}"
    .value="${labels}"
    id="${columnDetails.key}"
    .filterOptions="${dropdownOptions}"
    allItemsLabel="${allItemsLabel}"
    confirmActionButtonString="${confirmString}"
    ?canCopyContent="${columnDetails.canCopyContent}"
    copyContentMessage="${translateFn('Copied')}"
    @selectionChange="${e => modulesSelectionChangeHandler(gridController, columnDetails.key, e)}"
  ></gz-grid-cell-edit>`;
}

const modulesSelectionChangeHandler = (gridController, key, e) => {
  gridController?.view.updateKeyInEditedGridItemData(key, e.detail.selectedValues);
};
